import { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import API_URL from '../config/api';

function useSwipeNavigation(count) {
  const [current, setCurrent] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const hasPrev = current > 0;
  const hasNext = current < count - 1;

  const goPrev = useCallback(() => { if (current > 0) setCurrent(c => c - 1); }, [current]);
  const goNext = useCallback(() => { if (current < count - 1) setCurrent(c => c + 1); }, [current, count]);

  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
  };

  const onAreaClick = (e) => {
    if (count <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 2) goPrev();
    else goNext();
  };

  return { current, hasPrev, hasNext, goPrev, goNext, onTouchStart, onTouchEnd, onAreaClick };
}

export default function PhotoCarousel({ photos, fallbackUrl, className = '' }) {
  const stableIds = photos?.length > 0
    ? photos.map((p, i) => p.photo_id || p || `photo-${i}`)
    : fallbackUrl ? ['fallback'] : [];

  const urls = photos?.length > 0
    ? photos.map(p => `${API_URL}/api/photos/${p.photo_id || p}?v=${Date.now()}`)
    : fallbackUrl ? [fallbackUrl] : [];

  const { current, hasPrev, hasNext, goPrev, goNext, onTouchStart, onTouchEnd, onAreaClick } = useSwipeNavigation(urls.length);

  if (urls.length === 0) {
    return (
      <div className={`bg-[#F5F2EB] flex items-center justify-center ${className}`}>
        <span className="text-[#9C9C97] text-sm">No photos</span>
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden select-none ${className}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={onAreaClick}
      data-testid="photo-carousel"
    >
      {/* Photo */}
      <img
        src={urls[current]}
        alt=""
        className="w-full h-full object-cover transition-opacity duration-200"
        draggable={false}
      />

      {/* Dots indicator (top) */}
      {urls.length > 1 && (
        <div className="absolute top-3 left-0 right-0 flex justify-center gap-1 px-4" data-testid="carousel-dots">
          {urls.map((url, i) => (
            <div
              key={stableIds[i]}
              className={`h-[3px] rounded-full flex-1 transition-all duration-300 ${i === current ? 'bg-white' : 'bg-white/40'}`}
              style={{ maxWidth: `${100 / urls.length}%` }}
            />
          ))}
        </div>
      )}

      {/* Navigation arrows (desktop only, on hover) */}
      {urls.length > 1 && (
        <>
          {hasPrev && (
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white opacity-0 hover:opacity-100 transition-opacity"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white opacity-0 hover:opacity-100 transition-opacity"
              data-testid="carousel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </>
      )}

      {/* Counter (bottom right) */}
      {urls.length > 1 && (
        <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs font-medium rounded-full px-2.5 py-0.5">
          {current + 1}/{urls.length}
        </div>
      )}
    </div>
  );
}
